import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject, filter, Observable, throwError} from 'rxjs';
import {catchError, retry} from 'rxjs/operators';

import {
    // Core types
    PaginationResponse,
    ErrorResponse,

    // System interfaces
    ApiInfo,
    HealthStatus,
    KeycloakConfig,

    // User & auth interfaces
    User,
    UserCreateRequest,
    UserListResponse,
    TokenValidationRequest,
    TokenValidationResponse,
    UserInfoEnhanced,
    TwoFactorActivateRequest,
    TwoFactorActivateResponse,
    TwoFactorDeactivateResponse,
    PasswordResetRequest,
    PasswordResetResponse,
    RecoveryCodesResponse,

    // Course interfaces
    Course,
    CourseCreate,
    AssignedCoursesResponse,
    CourseStructure,
    CourseEnrollment,
    Lesson,

    // Quiz interfaces
    Quiz,
    QuizCreate,
    QuizAttempt,
    QuizAnswerSubmission,
    QuizAnswerFeedback,
    QuizNextInfo,
    QuizResults,
    QuizQuestion,

    // Progress & analytics
    ProgressData,
    ProgressSummary,
    LessonCompletionRequest,
    LessonCompletionResponse,
    Certificate,
    AnalyticsData,
    GeneralCoursesReport, DashboardMetrics
} from '../interfaces/api-interfaces';

// Import existing services for integration
import {KeycloakService} from './keycloak.service';
import {ToastService} from './toast.service';
import {EventTypes, OidcSecurityService, PublicEventsService} from "angular-auth-oidc-client";
import {environment} from "../../environments/environment.development";

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    private readonly baseUrl: string = this.getApiBaseUrl();
    private readonly maxRetries = 1;
    httpHeader = new HttpHeaders({})
    isHeaderSet: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

    /**
     * Get the appropriate API base URL based on environmentLocal
     */
    private getApiBaseUrl(): string {
        if (environment.production){
            return 'https://aims.masasana.ai/api';
        }
        return 'https://aims.stg.masasana.ai/api';
    }


    private http = inject(HttpClient);
    private keycloakService = inject(KeycloakService);
    private toastService = inject(ToastService);
    private oidcService = inject(OidcSecurityService);
    private eventService = inject(PublicEventsService);


    constructor() {
        this.eventService
            .registerForEvents()
            .pipe(filter((notification: any) => notification.type === EventTypes.NewAuthenticationResult))
            .subscribe(() => {
                this.buildHeader()
            });
    }

    buildHeader() {
        this.oidcService.getAccessToken().subscribe({
            next: (token: string) => {
                const currentUser = this.keycloakService.currentUser() as any;
                if (currentUser?.organization) {
                    this.httpHeader = this.httpHeader.set('X-Tenant-ID',currentUser.organization)
                } else {
                    this.httpHeader = this.httpHeader.set('X-Tenant-ID','Test')
                }
                this.httpHeader = this.httpHeader.set('Authorization', 'Bearer ' + token);
                console.info('Header set')
                if (!this.isHeaderSet.value) {
                    this.isHeaderSet.next(true)
                }
            }, error: err => this.isHeaderSet.next(false)
        })
    }


    /**
     * Build HTTP parameters from object
     */
    private buildParams(params?: { [key: string]: any }): HttpParams {
        let httpParams = new HttpParams();

        if (params) {
            Object.keys(params).forEach(key => {
                const value = params[key];
                if (value !== undefined && value !== null) {
                    httpParams = httpParams.set(key, value.toString());
                }
            });
        }
        return httpParams;
    }
    

    /**
     * Generic HTTP error handler
     */
    private readonly handleError = (error: HttpErrorResponse): Observable<never> => {
        let errorMessage = 'An unknown error occurred';
        let errorDetails: any = {};
        let toastTitle = 'Unknown Error';
        let toastSubtitle = 'An unknown error occurred';
        let showToast = true;

        if (error.error instanceof ErrorEvent) {
            // Client-side error (network issues, etc.)
            errorMessage = `Client Error: ${error.error.message}`;
            toastTitle = 'Client Error';
            toastSubtitle = error.error.message;
        } else {
            // Server-side error
            const baseMessage = error.error?.details || 'Unable to connect to server';
            errorMessage = `Server Error: ${error.status} ${error.statusText}`;
            errorDetails = error.error?.details || {};

            // Format structured error message
            if (error.status === 0) {
                toastTitle = 'Network Error';
                toastSubtitle = 'Unable to connect to server';
            } else if (error.status === 404) {
                // Don't show toast for 404s - these are often expected (deleted resources, etc.)
                showToast = false;
                toastTitle = 'Not Found';
                toastSubtitle = baseMessage;
            } else if (error.status >= 400 && error.status < 600) {
                toastTitle = `Response ${error.status}`;
                toastSubtitle = baseMessage;
            } else {
                toastTitle = `Error ${error.status}`;
                toastSubtitle = baseMessage;
            }
        }

        // Log error for debugging (less verbose for 404s)
        if (error.status === 404) {
            console.warn('Resource not found:', error.url);
        } else {
            console.error('API Error:', {
                status: error.status,
                message: errorMessage,
                details: errorDetails,
                url: error.url
            });
        }

        // Show structured error message with title and subtitle (skip for 404s)
        if (showToast) {
            this.toastService.error(toastTitle, toastSubtitle);
        }

        return throwError(() => ({
            error: errorMessage,
            status: error.status,
            details: errorDetails,
            timestamp: new Date().toISOString()
        } as ErrorResponse));
    }


    // =============================================================================
    // SYSTEM ENDPOINTS
    // =============================================================================

    /**
     * Get API information
     * GET /
     */
    getApiInfo(): Observable<ApiInfo> {
        return this.http.get<ApiInfo>(this.baseUrl + "/", {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Get health status
     * GET /health
     */
    getHealthStatus(): Observable<HealthStatus> {
        return this.http.get<HealthStatus>(this.baseUrl + "/health", {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    // =============================================================================
    // AUTHENTICATION ENDPOINTS
    // =============================================================================

    /**
     * Get Keycloak configuration
     * GET /auth/keycloak-config
     */
    getKeycloakConfig(): Observable<KeycloakConfig> {
        return this.http.get<KeycloakConfig>(this.baseUrl + "/auth/keycloak-config", {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Validate JWT token
     * POST /auth/validate-token
     */
    validateToken(tokenRequest: TokenValidationRequest): Observable<TokenValidationResponse> {
        return this.http.post<TokenValidationResponse>(this.baseUrl + "/auth/validate-token", tokenRequest, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Get enhanced user information with 2FA status and preferences
     * GET /auth/userinfo
     */
    getUserInfoEnhanced(): Observable<UserInfoEnhanced> {
        return this.http.get<UserInfoEnhanced>(this.baseUrl + "/auth/userinfo", {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Activate 2FA for current user
     * POST /auth/2fa/activate
     */
    activate2FA(request: TwoFactorActivateRequest): Observable<TwoFactorActivateResponse> {
        return this.http.post<TwoFactorActivateResponse>(this.baseUrl + "/auth/2fa/activate", request, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Deactivate 2FA for current user
     * DELETE /auth/2fa/deactivate
     */
    deactivate2FA(): Observable<TwoFactorDeactivateResponse> {
        return this.http.delete<TwoFactorDeactivateResponse>(this.baseUrl + "/auth/2fa/deactivate", {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Request password reset (no authentication required)
     * POST /auth/password-reset
     */
    requestPasswordReset(request: PasswordResetRequest): Observable<PasswordResetResponse> {
        return this.http.post<PasswordResetResponse>(this.baseUrl + "/auth/password-reset", request)
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Generate new recovery codes for 2FA
     * GET /auth/2fa/recovery-codes
     */
    getRecoveryCodes(): Observable<RecoveryCodesResponse> {
        return this.http.get<RecoveryCodesResponse>(this.baseUrl + "/auth/2fa/recovery-codes", {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }


    // =============================================================================
    // USER PROFILE ENDPOINTS
    // =============================================================================

    /**
     * Get current user profile
     * GET /user/profile
     */
    getUserProfile(token?:string): Observable<User> {
        let header = this.httpHeader;

        if (token) {
            header = this.httpHeader.set('Authorization', 'Bearer ' + token);
        }

        return this.http.get<User>(this.baseUrl + "/user/profile", {headers: header})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Update current user profile
     * PUT /user/profile
     */
    updateUserProfile(userData: User): Observable<User> {
        return this.http.put<User>(this.baseUrl + "/user/profile", userData, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    // =============================================================================
    // COURSE MANAGEMENT ENDPOINTS
    // =============================================================================

    /**
     * Get paginated list of courses
     * GET /courses
     */
    getCourses(params?: { skip?: number, limit?: number, search?: string, category?: string, status?: string }): Observable<PaginationResponse<Course>> {
        const httpParams = this.buildParams(params);
        return this.http.get<PaginationResponse<Course>>(this.baseUrl + "/courses", {
            params: httpParams,
            headers: this.httpHeader
        })
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Create a new course
     * POST /courses
     */
    createCourse(courseData: CourseCreate): Observable<Course> {
        return this.http.post<Course>(this.baseUrl + "/courses", courseData, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Get a specific course by ID
     * GET /courses/{course_id}
     */
    getCourseById(courseId: string): Observable<Course> {
        return this.http.get<Course>(this.baseUrl + `/courses/${courseId}`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Update a course
     * PUT /courses/{course_id}
     */
    updateCourse(courseId: string, courseData: CourseCreate): Observable<Course> {
        return this.http.put<Course>(this.baseUrl + `/courses/${courseId}`, courseData, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Delete a course
     * DELETE /courses/{course_id}
     */
    deleteCourse(courseId: string): Observable<{ message: string }> {
        return this.http.delete<{
            message: string
        }>(this.baseUrl + `/courses/${courseId}`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }



    // =============================================================================
    // COURSE-SCOPED LESSON ENDPOINTS
    // =============================================================================

    /**
     * Get the content of a specific lesson with polymorphic type support
     * GET /courses/{course_id}/lessons/{lesson_id}
     */
    getCourseLesson(courseId: string, lessonId: string): Observable<Lesson> {
        return this.http.get<Lesson>(this.baseUrl + `/courses/${courseId}/lessons/${lessonId}`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    // =============================================================================
    // QUIZ MANAGEMENT ENDPOINTS
    // =============================================================================

    /**
     * Get paginated list of quizzes
     * GET /quiz
     */
    getQuizzes(params?: { skip?: number, limit?: number }): Observable<PaginationResponse<Quiz>> {
        const httpParams = this.buildParams(params);
        return this.http.get<PaginationResponse<Quiz>>(this.baseUrl + "/quiz", {
            params: httpParams,
            headers: this.httpHeader
        })
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Create a new quiz
     * POST /quiz
     */
    createQuiz(quizData: QuizCreate): Observable<Quiz> {
        return this.http.post<Quiz>(this.baseUrl + "/quiz", quizData, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Get a specific quiz by ID
     * GET /quiz/{quiz_id}
     */
    getQuizById(quizId: string): Observable<Quiz> {
        return this.http.get<Quiz>(this.baseUrl + `/quiz/${quizId}`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }


    /**
     * Delete a quiz
     * DELETE /quiz/{quiz_id}
     */
    deleteQuiz(quizId: string): Observable<{ message: string }> {
        return this.http.delete<{ message: string }>(this.baseUrl + `/quiz/${quizId}`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }


    // =============================================================================
    // PROGRESS TRACKING ENDPOINTS
    // =============================================================================

    /**
     * Get the current user's progress in a specific course
     * GET /courses/{course_id}/progress
     */
    getCourseProgress(courseId: string): Observable<ProgressData> {
        return this.http.get<ProgressData>(this.baseUrl + `/courses/${courseId}/progress`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Mark a lesson as complete
     * POST /courses/{course_id}/lessons/{lesson_id}/complete
     */
    markLessonComplete(courseId: string, lessonId: string, completionData?: LessonCompletionRequest): Observable<LessonCompletionResponse> {
        return this.http.post<LessonCompletionResponse>(
            this.baseUrl + `/courses/${courseId}/lessons/${lessonId}/complete`,
            completionData || {},
            {headers: this.httpHeader}
        ).pipe(
            catchError(this.handleError),
            retry(this.maxRetries)
        );
    }

    /**
     * Reset the current user's progress in a specific course
     * PUT /courses/{course_id}/progress/reset
     */
    resetCourseProgress(courseId: string): Observable<{ message: string }> {
        return this.http.put<{ message: string }>(
            this.baseUrl + `/courses/${courseId}/progress/reset`,
            {},
            {headers: this.httpHeader}
        ).pipe(
            catchError(this.handleError),
            retry(this.maxRetries)
        );
    }

    /**
     * Get courses assigned/enrolled to current user with progress
     * GET /courses/assigned
     */
    getAssignedCourses(params?: { status?: 'not_started' | 'in_progress' | 'completed', skip?: number, limit?: number }): Observable<AssignedCoursesResponse> {
        const httpParams = this.buildParams(params);
        return this.http.get<AssignedCoursesResponse>(this.baseUrl + "/courses/assigned", {
            params: httpParams,
            headers: this.httpHeader
        })
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Get course structure with navigation info
     * GET /courses/{course_id}/structure
     */
    getCourseStructure(courseId: string): Observable<CourseStructure> {
        return this.http.get<CourseStructure>(this.baseUrl + `/courses/${courseId}/structure`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Get the current user's progress across all enrolled courses
     * GET /progress/courses
     */
    getAllCourseProgress(): Observable<ProgressSummary> {
        return this.http.get<ProgressSummary>(this.baseUrl + `/progress/courses`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    // =============================================================================
    // COURSE ENROLLMENT ENDPOINTS
    // =============================================================================

    /**
     * Enroll user in a course
     * POST /courses/{course_id}/enroll
     */
    enrollInCourse(courseId: string): Observable<CourseEnrollment> {
        return this.http.post<CourseEnrollment>(
            this.baseUrl + `/courses/${courseId}/enroll`,
            {},
            {headers: this.httpHeader}
        ).pipe(
            catchError(this.handleError),
            retry(this.maxRetries)
        );
    }

    // =============================================================================
    // QUIZ ATTEMPT MANAGEMENT ENDPOINTS
    // =============================================================================

    /**
     * Initialize a new quiz attempt for a QuizLesson
     * POST /courses/{course_id}/lessons/{lesson_id}/start-quiz
     */
    startQuizSession(courseId: string, lessonId: string): Observable<QuizAttempt> {
        return this.http.post<QuizAttempt>(
            this.baseUrl + `/courses/${courseId}/lessons/${lessonId}/start-quiz`,
            {},
            {headers: this.httpHeader}
        ).pipe(
            catchError(this.handleError),
            retry(this.maxRetries)
        );
    }

    /**
     * Retrieve a question with randomized answers for the current attempt
     * GET /quiz-attempts/{attempt_id}/questions/{question_id}
     */
    getQuizQuestion(attemptId: string, questionId: string): Observable<QuizQuestion> {
        return this.http.get<QuizQuestion>(this.baseUrl + `/quiz-attempts/${attemptId}/questions/${questionId}`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Submit user's answer and get immediate feedback
     * POST /quiz-attempts/{attempt_id}/questions/{question_id}
     */
    submitQuizAnswer(attemptId: string, questionId: string, answerData: QuizAnswerSubmission): Observable<QuizAnswerFeedback> {
        return this.http.post<QuizAnswerFeedback>(
            this.baseUrl + `/quiz-attempts/${attemptId}/questions/${questionId}`,
            answerData,
            {headers: this.httpHeader}
        ).pipe(
            catchError(this.handleError),
            retry(this.maxRetries)
        );
    }

    /**
     * Get the next question ID or quiz completion status
     * GET /quiz-attempts/{attempt_id}/next
     */
    getNextQuizQuestion(attemptId: string): Observable<QuizNextInfo> {
        return this.http.get<QuizNextInfo>(this.baseUrl + `/quiz-attempts/${attemptId}/next`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Get complete quiz results after all questions are completed
     * GET /quiz-attempts/{attempt_id}/report
     */
    getQuizResults(attemptId: string): Observable<QuizResults> {
        return this.http.get<QuizResults>(this.baseUrl + `/quiz-attempts/${attemptId}/report`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    // =============================================================================
    // CERTIFICATES ENDPOINTS
    // =============================================================================

    /**
     * Get certificates earned by current user
     * GET /certificates
     */
    getCertificates(): Observable<Certificate[]> {
        return this.http.get<Certificate[]>(this.baseUrl + `/certificates`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }



    getCertificateDocument(courseId: string) {
        return this.http.get(this.baseUrl + `/certificates/course/${courseId}`, {headers: this.httpHeader, responseType: "blob"})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    // =============================================================================
    // ANALYTICS & REPORTING ENDPOINTS
    // =============================================================================

    /**
     * Get analytics data for a specific course (requires management role)
     * GET /reports/course/{course_id}/analytics
     */
    getCourseAnalytics(courseId: string): Observable<AnalyticsData> {
        return this.http.get<AnalyticsData>(this.baseUrl + `/reports/course/${courseId}/analytics`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    getReport(): Observable<GeneralCoursesReport>{
        return this.http.get<GeneralCoursesReport>(this.baseUrl + `/reports/courses`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    getReportDashboard(){
        return this.http.get<DashboardMetrics>(this.baseUrl + `/reports/dashboard`, {headers: this.httpHeader})
    }

    // =============================================================================
    // USER MANAGEMENT ENDPOINTS
    // =============================================================================

    /**
     * Create user in both Keycloak and database (requires management role)
     * POST /users
     */
    createUser(userData: UserCreateRequest): Observable<User> {
        return this.http.post<User>(this.baseUrl + "/users", userData, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Get all users belonging to the same organizations as current user
     * GET /users/organization
     */
    getOrganizationUsers(): Observable<UserListResponse> {
        return this.http.get<UserListResponse>(this.baseUrl + "/users/organization", {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

    /**
     * Delete user from Keycloak and database (self or management role required)
     * DELETE /users/{user_id}
     */
    deleteUser(userId: string): Observable<{message: string}> {
        return this.http.delete<{message: string}>(this.baseUrl + `/users/${userId}`, {headers: this.httpHeader})
            .pipe(
                catchError(this.handleError),
                retry(this.maxRetries)
            );
    }

}